import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, App, Alert } from 'antd';
import { UserOutlined, LockOutlined, BulbOutlined, BulbFilled, WarningOutlined } from '@ant-design/icons';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../../hooks/useAuth.tsx';
import { useTheme } from '../../contexts/useTheme';
import { getRecaptchaConfig } from '../../services/clientConfigService';

const RECAPTCHA_ACTION = 'signin';
const recaptchaConfig = getRecaptchaConfig();

// Debug info from 401 redirect
interface SessionExpiredInfo {
  tokenInfo?: {
    timestamp: string;
    isExpired: boolean;
    expiresAt: string;
    expiresIn: string;
    issuedAt?: string;
  };
  requestLog?: Array<{
    timestamp: string;
    url: string;
    method: string;
    baseURL?: string;
    hasAuthHeader?: boolean;
    authHeaderPreview?: string;
    serverResponse?: string;
  }>;
}

export const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sessionExpiredInfo, setSessionExpiredInfo] = useState<SessionExpiredInfo | null>(null);
  const { login } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { message } = App.useApp();
  const { theme, toggleTheme } = useTheme();
  
  // Check for 401 redirect debug info on mount
  useEffect(() => {
    try {
      const tokenInfo = sessionStorage.getItem('401_token_info');
      const requestLog = sessionStorage.getItem('401_debug_log');
      
      if (tokenInfo || requestLog) {
        const info: SessionExpiredInfo = {};
        if (tokenInfo) {
          info.tokenInfo = JSON.parse(tokenInfo);
        }
        if (requestLog) {
          info.requestLog = JSON.parse(requestLog);
        }
        setSessionExpiredInfo(info);
        
        // Clear the debug info after reading
        sessionStorage.removeItem('401_token_info');
        sessionStorage.removeItem('401_debug_log');
        
        // Log to console for easier debugging
        console.log('[LoginPage] Session expired debug info:', info);
      }
    } catch {
      // Ignore parsing errors
    }
  }, []);

  const handleFormSubmit = async () => {
    // Only check for reCAPTCHA if it's enabled and configured
    const isRecaptchaActive = recaptchaConfig.enabled && recaptchaConfig.siteKey;
    if (isRecaptchaActive && !executeRecaptcha) {
      message.error('reCAPTCHA not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    try {
      // Execute reCAPTCHA only if enabled and configured
      const recaptchaToken = isRecaptchaActive && executeRecaptcha
        ? await executeRecaptcha(RECAPTCHA_ACTION)
        : '';

      // Attempt login
      const loggedInUser = await login(email, password, recaptchaToken);
      
      message.success(`Welcome back, ${loggedInUser.firstName} ${loggedInUser.lastName}!`);
      // Navigation will be handled automatically by PublicRoute after authState updates
    } catch (error) {
      console.error('Login error:', error);
      message.error(
        error instanceof Error 
          ? error.message 
          : 'Your sign-in details are incorrect. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="login-container"
      style={{
        background: theme === 'dark' ? '#141414' : '#f0f2f5',
      }}
    >
      <Card 
        className="login-card"
        style={{
          background: theme === 'dark' ? '#1f1f1f' : '#ffffff',
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        }}
      >
        {/* Logos Section */}
        <div style={{ 
          marginBottom: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}>
          {/* Logos Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {/* FIA Logo */}
            <div style={{
              background: '#102D5C',
              padding: '10px 20px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img 
                src="/fia-logo.svg" 
                alt="FIA Logo" 
                style={{ 
                  height: '40px',
                  width: 'auto',
                }} 
              />
            </div>

            {/* Tomorrow.io Logo */}
            <img 
              src={theme === 'dark' ? '/tio-logo-white.svg' : '/tio-logo-dark.svg'}
              alt="Tomorrow.io Logo" 
              style={{ 
                width: '140px',
                height: 'auto',
                opacity: 0.9,
              }} 
            />
          </div>

          {/* Title */}
          <h2 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
          }}>
            FIA Weather Portal - Login
          </h2>
        </div>

        {/* Session Expired Debug Info */}
        {sessionExpiredInfo && (
          <Alert
            title="Session Expired"
            description={
              <div style={{ fontSize: '12px' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  Your session expired and you were redirected to login.
                </p>
                {sessionExpiredInfo.tokenInfo && (
                  <div style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    marginBottom: '8px',
                  }}>
                    <div><strong>Token Info:</strong></div>
                    <div>Expired: {sessionExpiredInfo.tokenInfo.isExpired ? 'Yes' : 'No'}</div>
                    <div>Expires: {sessionExpiredInfo.tokenInfo.expiresIn}</div>
                    <div>Expires At: {sessionExpiredInfo.tokenInfo.expiresAt}</div>
                    {sessionExpiredInfo.tokenInfo.issuedAt && (
                      <div>Issued At: {sessionExpiredInfo.tokenInfo.issuedAt}</div>
                    )}
                  </div>
                )}
                {sessionExpiredInfo.requestLog && sessionExpiredInfo.requestLog.length > 0 && (
                  <div style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    maxHeight: '100px',
                    overflow: 'auto',
                  }}>
                    <div><strong>Failed Requests ({sessionExpiredInfo.requestLog.length}):</strong></div>
                    {sessionExpiredInfo.requestLog.slice(-3).map((req, i) => (
                      <div key={i} style={{ marginTop: '4px' }}>
                        <div>{req.method?.toUpperCase()} {req.url}</div>
                        <div style={{ color: req.hasAuthHeader ? '#52c41a' : '#ff4d4f', fontSize: '10px' }}>
                          Auth: {req.hasAuthHeader ? '✓ Present' : '✗ MISSING'} 
                          {req.authHeaderPreview && ` (${req.authHeaderPreview})`}
                        </div>
                        {req.serverResponse && (
                          <div style={{ color: '#ff7875', fontSize: '10px', wordBreak: 'break-all' }}>
                            Server: {req.serverResponse}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            closable
            onClose={() => setSessionExpiredInfo(null)}
            style={{ marginBottom: '16px' }}
          />
        )}

        <Form
          name="login"
          onFinish={handleFormSubmit}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Login
            </Button>
          </Form.Item>

          {/* Theme Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <span style={{
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
              fontSize: '14px',
            }}>
              Theme:
            </span>
            <Button
              type="default"
              icon={theme === 'dark' ? <BulbFilled style={{ color: '#8c8c8c' }} /> : <BulbOutlined />}
              onClick={toggleTheme}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {theme === 'dark' ? 'Dark' : 'Light'}
            </Button>
          </div>

          {recaptchaConfig.enabled && recaptchaConfig.siteKey && (
            <div style={{ 
              textAlign: 'center', 
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : '#888', 
              fontSize: '12px' 
            }}>
              This site is protected by reCAPTCHA
            </div>
          )}
        </Form>
      </Card>
    </div>
  );
};
